import { Injectable } from '@angular/core'
import { PlanType } from 'formwork-planner-lib'
import { Plan } from '../models/plan'
import { Translation } from './translation.service'
import { ZoomAndPanService } from './zoom-and-pan.service'
import {
  PlanCreateCommandParams,
  PlanType as ApiPlanType,
  NavStep,
  PlansModel,
  ProjectsModel,
} from '../../generated/efp-api'
import { PlanCreationRepository } from '../repositories/plan-creation.repository'
import { Router } from '@angular/router'
import { WidgetService } from './widget.service'
import { Project } from '../models/project'

@Injectable({
  providedIn: 'root',
})
export class PlanService {
  constructor(
    private readonly translation: Translation,
    private readonly router: Router,
    private readonly zoomAndPanService: ZoomAndPanService,
    private readonly planCreationRepository: PlanCreationRepository,
    private readonly widgetService: WidgetService
  ) {}

  public static mapModelToProject(model: ProjectsModel): Project {
    return {
      id: model.id,
      name: model.name,
      stockId: model.stockId ?? null,
      plans: model.plans?.map(PlanService.mapPlanModelToPlan) ?? ([] as Plan[]),
      defaultPlanSettings: model.defaultPlanSettingsId,
      defaultProject: model.defaultProject ?? undefined,
      accId: model.accId ?? undefined,
      accHubId: model.accHubId ?? undefined,
      doka360ProjectUid: model.doka360ProjectUid ?? undefined,
    }
  }

  public static mapPlanModelToPlan(model: PlansModel): Plan {
    return {
      id: model.id,
      name: model.name,
      date: new Date(model.date),
      lastUsed: new Date(model.lastUsed),
      buildingType: model.buildingType === 'SLAB' ? PlanType.SLAB : PlanType.WALL,
      projectId: model.projectId,
      settingsId: model.settingsId,
      currentStep: model.currentStep,
      stockId: model.stockId ?? null,
      serializedMesh: model.serializedMesh ?? undefined,
      doka360ProjectUid: model.doka360ProjectUid ?? undefined,
      isCompleted: model.isCompleted ?? undefined,
    }
  }

  public async createPlan(
    planType: string,
    projectId?: number,
    planName?: string,
    shouldOpenInNewTab = false
  ): Promise<void> {
    let newPlanName = planName
    if (!newPlanName) {
      const translationKey =
        planType === PlanType.WALL ? 'HOME.QUICK_WALL_PREFIX' : 'HOME.QUICK_CEILING_PREFIX'
      newPlanName = this.translation.translate(translationKey)
    }

    const createPlanDto: PlanCreateCommandParams = {
      name: newPlanName,
      addNextPlanNumberToName: planName ? false : true,
      buildingType: planType === PlanType.WALL ? ApiPlanType.Wall : ApiPlanType.Slab,
      projectId,
      currentStep: NavStep.Plansettings,
    }

    const plan = await this.planCreationRepository.createPlan(createPlanDto)

    if (shouldOpenInNewTab) {
      const urlPath = this.router.serializeUrl(
        this.router.createUrlTree(['plansettings/' + plan.id])
      )
      this.widgetService.navigateInNewWebTab(urlPath)
    } else {
      this.beforeOpeningPlan()
      await this.router.navigate(['plansettings/' + plan.id], {
        queryParams: { backUrl: '/homepage' },
      })
    }
  }

  private beforeOpeningPlan(): void {
    this.zoomAndPanService.reset()
  }
}
